/**
 * Inventory › Unit conversions (تبدیل واحدها).
 * UI_REFERENCE_SOURCE: Sakhtar Cloud ERP v2 — inventory list archetype (V2DataGrid) with
 * V2Modal forms; docs/design-system/V2_USAGE.md §4 and §6.
 */
import { useMemo, useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import {
  LoadingState,
  StatePanel,
  V2Alert,
  V2Badge,
  V2Button,
  V2Card,
  V2CardBody,
  V2CardHeader,
  V2DataGrid,
  V2EmptyState,
  V2FormField,
  V2Modal,
  V2StatusStrip,
  V2ToastStack,
  type V2GridColumn,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { apiPost } from '../../lib/apiClient.js';
import { bannerMessageFrom } from '../../lib/formErrors.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { faDigits } from '../../lib/faDigits.js';
import { toEnglishDigits } from '../../lib/phoneNumber.js';
import type { MasterDataListResponse } from '../../lib/types/masterData.js';
import { formatQuantity, isTruncated, pickerOptions } from './inventoryDisplay.js';
import { useToasts } from './useInventoryUi.js';
import './inventory.css';

interface UomConversionItem {
  readonly conversion_id: string;
  readonly product_id: string | null;
  readonly product_code: string | null;
  readonly product_name: string | null;
  readonly from_uom_id: string;
  readonly from_uom_code: string;
  readonly from_uom_name: string;
  readonly to_uom_id: string;
  readonly to_uom_code: string;
  readonly to_uom_name: string;
  readonly factor_numerator: string;
  readonly factor_denominator: string;
  readonly status: string;
}

interface ConversionsResponse {
  readonly items: readonly UomConversionItem[];
}

const PICKER_LIMIT = 200;

/** A positive whole number written with Persian or Latin digits; `null` when it is anything else. */
function positiveInteger(raw: string): string | null {
  const text = toEnglishDigits(raw).trim();
  return /^\d+$/.test(text) && BigInt(text) > 0n ? text.replace(/^0+/, '') : null;
}

export function UomConversionsPage() {
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const { toasts, push, dismiss } = useToasts();

  const [productFilter, setProductFilter] = useState('');

  // New conversion dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [newProductId, setNewProductId] = useState('');
  const [newFromUomId, setNewFromUomId] = useState('');
  const [newToUomId, setNewToUomId] = useState('');
  const [newFactorNum, setNewFactorNum] = useState('1');
  const [newFactorDen, setNewFactorDen] = useState('1');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Converter
  const [calcProductId, setCalcProductId] = useState('');
  const [calcFromUomId, setCalcFromUomId] = useState('');
  const [calcQty, setCalcQty] = useState('1');
  const [calcResult, setCalcResult] = useState<string | null>(null);
  const [calcError, setCalcError] = useState<string | null>(null);
  const [calcLoading, setCalcLoading] = useState(false);

  const url = useMemo(() => {
    if (!slug) return null;
    const params = new URLSearchParams();
    if (productFilter) params.set('productId', productFilter);
    return `${base}/api/v1/inventory/uom-conversions?${params.toString()}`;
  }, [base, productFilter, slug]);

  const result = useApiGet<ConversionsResponse>(url, [url]);
  const products = useApiGet<MasterDataListResponse>(
    slug ? `${base}/api/v1/master-data/products?limit=${PICKER_LIMIT}` : null,
    [slug],
  );
  const uoms = useApiGet<MasterDataListResponse>(
    slug ? `${base}/api/v1/master-data/uoms?limit=${PICKER_LIMIT}` : null,
    [slug],
  );

  const productOptions = useMemo(
    () => (products.status === 'ready' ? pickerOptions(products.data.items, 'productId') : []),
    [products],
  );
  const productsTruncated =
    products.status === 'ready' && isTruncated(products.data.items.length, products.data.total);
  const uomOptions = useMemo(
    () => (uoms.status === 'ready' ? pickerOptions(uoms.data.items, 'uomId') : []),
    [uoms],
  );

  const openCreate = () => {
    setNewProductId('');
    setNewFromUomId('');
    setNewToUomId('');
    setNewFactorNum('1');
    setNewFactorDen('1');
    setFormError(null);
    setCreateOpen(true);
  };

  const handleCreateConversion = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    if (!newFromUomId || !newToUomId) {
      setFormError('واحد مبدأ و واحد مقصد را انتخاب کنید.');
      return;
    }
    if (newFromUomId === newToUomId) {
      setFormError('واحد مبدأ و مقصد نباید یکسان باشند.');
      return;
    }
    const numerator = positiveInteger(newFactorNum);
    const denominator = positiveInteger(newFactorDen);
    if (!numerator || !denominator) {
      setFormError('صورت و مخرج ضریب باید عدد صحیح مثبت باشند.');
      return;
    }
    setSubmitting(true);
    setFormError(null);
    try {
      await apiPost(`${base}/api/v1/inventory/uom-conversions`, {
        productId: newProductId || undefined,
        fromUomId: newFromUomId,
        toUomId: newToUomId,
        factorNumerator: numerator,
        factorDenominator: denominator,
      });
      setCreateOpen(false);
      push('ضریب تبدیل ثبت شد.');
      result.reload();
    } catch (err: unknown) {
      setFormError(bannerMessageFrom(err, 'ثبت ضریب تبدیل انجام نشد.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleTestConversion = async (e: FormEvent) => {
    e.preventDefault();
    if (calcLoading) return;
    const quantity = positiveInteger(calcQty);
    if (!calcProductId || !calcFromUomId || !quantity) {
      setCalcResult(null);
      setCalcError('کالا، واحد ورودی و یک مقدار صحیح مثبت را وارد کنید.');
      return;
    }
    setCalcLoading(true);
    setCalcResult(null);
    setCalcError(null);
    try {
      const res = await apiPost<{ baseQuantity: { numerator: string; denominator: string } }>(
        `${base}/api/v1/inventory/uom-conversions/convert`,
        {
          productId: calcProductId,
          fromUomId: calcFromUomId,
          quantity: { numerator: quantity, denominator: '1' },
        },
      );
      setCalcResult(formatQuantity(res.baseQuantity));
    } catch (err: unknown) {
      setCalcError(bannerMessageFrom(err, 'تبدیل انجام نشد.'));
    } finally {
      setCalcLoading(false);
    }
  };

  const columns: V2GridColumn<UomConversionItem>[] = [
    {
      key: 'product',
      header: 'دامنهٔ کالا',
      wrap: true,
      render: (r) =>
        r.product_name ? (
          <span className="inv-cell">
            <span className="inv-cell__main">{r.product_name}</span>
            <span className="inv-cell__sub ltr">{r.product_code}</span>
          </span>
        ) : (
          <V2Badge tone="neutral">سراسری (همهٔ کالاها)</V2Badge>
        ),
    },
    {
      key: 'from_uom',
      header: 'واحد مبدأ',
      render: (r) => r.from_uom_name,
    },
    {
      key: 'to_uom',
      header: 'واحد مقصد',
      render: (r) => r.to_uom_name,
    },
    {
      key: 'factor',
      header: 'ضریب',
      align: 'end',
      emphasis: 'key',
      render: (r) => (
        <span className="sk-num">
          {formatQuantity({ numerator: r.factor_numerator, denominator: r.factor_denominator })}
        </span>
      ),
    },
    {
      key: 'formula',
      header: 'معادل',
      muted: true,
      wrap: true,
      render: (r) => (
        <span>
          {faDigits(1)} {r.from_uom_name} ={' '}
          {formatQuantity({ numerator: r.factor_numerator, denominator: r.factor_denominator })}{' '}
          {r.to_uom_name}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'وضعیت',
      render: (r) => (
        <V2Badge tone={r.status === 'active' ? 'success' : 'neutral'}>
          {r.status === 'active' ? 'فعال' : 'غیرفعال'}
        </V2Badge>
      ),
    },
  ];

  if (!slug) return null;

  const count = result.status === 'ready' ? result.data.items.length : 0;
  const productPicker = (
    <select
      className="sk-select inv-toolbar-select"
      aria-label="فیلتر کالا"
      value={productFilter}
      onChange={(e) => setProductFilter(e.target.value)}
    >
      <option value="">همهٔ کالاها و ضرایب سراسری</option>
      {productOptions.map((p) => (
        <option key={p.value} value={p.value}>
          {p.label}
        </option>
      ))}
    </select>
  );

  return (
    <div className="inv-page">
      {result.status === 'loading' ? <LoadingState /> : null}
      {result.status === 'error' ? (
        <StatePanel
          variant={result.error.status === 403 ? 'permission_denied' : 'network_error'}
          title={
            result.error.status === 403
              ? 'دسترسی مشاهدهٔ ضرایب تبدیل را ندارید'
              : 'ضرایب تبدیل قابل نمایش نیستند'
          }
        />
      ) : null}

      {result.status === 'ready' ? (
        <V2DataGrid
          icon="layers"
          title="ضرایب تبدیل واحد"
          subtitle={`${faDigits(count)} ضریب`}
          toolbarExtra={productOptions.length > 0 ? productPicker : undefined}
          actions={
            <V2Button variant="primary" icon="plus" onClick={openCreate}>
              ثبت ضریب تبدیل
            </V2Button>
          }
          columns={columns}
          rows={result.data.items}
          rowKey={(r) => r.conversion_id}
          zebra
          storageKey="inventory.uom-conversions"
          emptyState={
            <V2EmptyState
              icon="layers"
              title="ضریب تبدیلی ثبت نشده است"
              description="ضریبی با این فیلتر وجود ندارد."
              compact
            />
          }
        />
      ) : null}

      <V2Card>
        <V2CardHeader
          icon="settings"
          title="آزمونگر تبدیل به واحد پایه"
          subtitle="مقدار یک کالا را با ضرایب ثبت‌شده به واحد پایه تبدیل می‌کند؛ محاسبه دقیق و کسری است."
        />
        <V2CardBody>
          <form className="inv-form" noValidate onSubmit={handleTestConversion}>
            <div className="inv-form__row inv-form__row--3">
              <V2FormField label="کالا" required width="full">
                <select
                  className="sk-select"
                  value={calcProductId}
                  onChange={(e) => setCalcProductId(e.target.value)}
                >
                  <option value="">انتخاب کالا…</option>
                  {productOptions.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </V2FormField>
              <V2FormField label="واحد ورودی" required width="full">
                <select
                  className="sk-select"
                  value={calcFromUomId}
                  onChange={(e) => setCalcFromUomId(e.target.value)}
                >
                  <option value="">انتخاب واحد…</option>
                  {uomOptions.map((u) => (
                    <option key={u.value} value={u.value}>
                      {u.label}
                    </option>
                  ))}
                </select>
              </V2FormField>
              <V2FormField label="مقدار" required width="full">
                <input
                  className="sk-input ltr sk-num"
                  inputMode="numeric"
                  value={calcQty}
                  onChange={(e) => setCalcQty(e.target.value)}
                />
              </V2FormField>
            </div>
            {calcError ? <V2Alert tone="danger">{calcError}</V2Alert> : null}
            {calcResult !== null ? (
              <V2StatusStrip
                tone="success"
                title="مقدار معادل در واحد پایه"
                valueLabel="نتیجه"
                value={<span className="ltr">{calcResult}</span>}
              />
            ) : null}
            <div className="inv-form__actions">
              <V2Button type="submit" variant="secondary" disabled={calcLoading}>
                {calcLoading ? 'در حال محاسبه…' : 'محاسبهٔ مقدار پایه'}
              </V2Button>
            </div>
          </form>
        </V2CardBody>
      </V2Card>

      <V2Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        dismissible={!submitting}
        title="ثبت ضریب تبدیل واحد"
        footer={
          <>
            <V2Button variant="ghost" disabled={submitting} onClick={() => setCreateOpen(false)}>
              انصراف
            </V2Button>
            <V2Button type="submit" form="uom-create-form" variant="primary" disabled={submitting}>
              {submitting ? 'در حال ثبت…' : 'ثبت ضریب'}
            </V2Button>
          </>
        }
      >
        <form
          id="uom-create-form"
          className="inv-form"
          noValidate
          onSubmit={handleCreateConversion}
        >
          {formError ? <V2Alert tone="danger">{formError}</V2Alert> : null}
          <V2FormField
            label="کالا"
            width="full"
            hint={
              productsTruncated
                ? `فقط ${faDigits(PICKER_LIMIT)} کالای اول فهرست نمایش داده می‌شود.`
                : 'خالی بگذارید تا ضریب برای همهٔ کالاها اعمال شود.'
            }
          >
            <select
              className="sk-select"
              value={newProductId}
              onChange={(e) => setNewProductId(e.target.value)}
            >
              <option value="">ضریب سراسری برای همهٔ کالاها</option>
              {productOptions.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </V2FormField>
          <div className="inv-form__row">
            <V2FormField label="از واحد" required width="full">
              <select
                className="sk-select"
                value={newFromUomId}
                onChange={(e) => setNewFromUomId(e.target.value)}
              >
                <option value="">انتخاب واحد…</option>
                {uomOptions.map((u) => (
                  <option key={u.value} value={u.value}>
                    {u.label}
                  </option>
                ))}
              </select>
            </V2FormField>
            <V2FormField label="به واحد" required width="full">
              <select
                className="sk-select"
                value={newToUomId}
                onChange={(e) => setNewToUomId(e.target.value)}
              >
                <option value="">انتخاب واحد…</option>
                {uomOptions.map((u) => (
                  <option key={u.value} value={u.value}>
                    {u.label}
                  </option>
                ))}
              </select>
            </V2FormField>
          </div>
          <div className="inv-form__group">
            <p className="inv-form__note">
              ضریب کسری: مقدار مقصد = مقدار مبدأ × صورت ÷ مخرج. مثلاً هر کارتن ۱۲ عدد: صورت ۱۲ و
              مخرج ۱.
            </p>
            <div className="inv-form__row">
              <V2FormField label="صورت کسر" required width="full">
                <input
                  className="sk-input ltr sk-num"
                  inputMode="numeric"
                  value={newFactorNum}
                  onChange={(e) => setNewFactorNum(e.target.value)}
                />
              </V2FormField>
              <V2FormField label="مخرج کسر" required width="full">
                <input
                  className="sk-input ltr sk-num"
                  inputMode="numeric"
                  value={newFactorDen}
                  onChange={(e) => setNewFactorDen(e.target.value)}
                />
              </V2FormField>
            </div>
          </div>
        </form>
      </V2Modal>

      <V2ToastStack toasts={toasts} onDismiss={dismiss} />
    </div>
  );
}
